import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { registerServiceWorker, subscribeToPush, isPushSupported } from '../lib/push-service';
import { getAcquisition } from '../lib/acquisition';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('ccc_token'));
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const initDone = React.useRef(false);

  const setAuthHeader = useCallback((t) => {
    if (t) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${t}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('ccc_token');
    setToken(null);
    setAuthHeader(null);
    setUser(null);
  }, [setAuthHeader]);

  // Intercept 401 responses → session invalidated by another login
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && token && user) {
          logout();
          setSessionExpired(true);
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [token, user, logout]);

  const setupPushNotifications = useCallback(async (authToken) => {
    if (!isPushSupported()) return;
    // Small delay to avoid blocking login flow
    setTimeout(async () => {
      try {
        await subscribeToPush(authToken);
      } catch (e) {
        console.warn('Push setup deferred:', e);
      }
    }, 2000);
  }, []);

  // On mount, check if token is valid
  useEffect(() => {
    if (initDone.current) return;
    const init = async () => {
      if (!token) { setLoading(false); return; }
      setAuthHeader(token);
      try {
        const res = await axios.get(`${API_URL}/auth/me`);
        setUser(res.data.user);
        setupPushNotifications(token);
      } catch {
        // Token invalid or session expired
        localStorage.removeItem('ccc_token');
        setToken(null);
        setAuthHeader(null);
      }
      setLoading(false);
    };
    init();
    initDone.current = true;
  }, [token, setAuthHeader, setupPushNotifications]);

  // Register SW on mount, subscribe to push when authenticated
  useEffect(() => {
    registerServiceWorker();
  }, []);

  const postWithRetry = async (url, payload, attempts = 3) => {
    let lastErr;
    for (let i = 0; i < attempts; i++) {
      try {
        return await axios.post(url, payload, { timeout: 15000 });
      } catch (err) {
        lastErr = err;
        const retriable = !err.response || err.code === 'ECONNABORTED' || err.response.status >= 502;
        if (!retriable || i === attempts - 1) throw err;
        await new Promise((r) => setTimeout(r, 1500 * (i + 1)));
      }
    }
    throw lastErr;
  };

  const login = async (email, password) => {
    const res = await postWithRetry(`${API_URL}/auth/login`, { email, password });
    const { token: newToken, user: userData } = res.data;
    localStorage.setItem('ccc_token', newToken);
    setToken(newToken);
    setAuthHeader(newToken);
    setUser(userData);
    setupPushNotifications(newToken);
    return userData;
  };

  const register = async (email, password, name, userProfile = null) => {
    const res = await postWithRetry(`${API_URL}/auth/register`, { email, password, name, user_profile: userProfile, acquisition: getAcquisition() });
    // Vérification email : le compte n'existe pas encore, aucune session à poser
    return res.data;
  };

  // Pose une session obtenue hors login/register (ex: activation après vérification email)
  const applySession = (newToken, userData) => {
    localStorage.setItem('ccc_token', newToken);
    setToken(newToken);
    setAuthHeader(newToken);
    setUser(userData);
    setupPushNotifications(newToken);
    return userData;
  };

  const dismissSessionExpired = () => setSessionExpired(false);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, applySession, logout, isAuthenticated: !!user, sessionExpired, dismissSessionExpired }}>
      {children}
    </AuthContext.Provider>
  );
};
